import { isLocalEnv } from '@/utils/getCurrentEnv';

/**
 * Debug-output gate for Terra Nova apps, decoupled from environment identity.
 *
 * Debug-only behavior (GraphQL `apiCalls` extensions, error payloads, Apollo
 * sandbox landing pages, client dev tooling) used to be gated on `!isProd()`,
 * which enables it in every non-prod environment — including performance
 * benchmarking environments where the extra output skews results against prod.
 * `isAppDebug()` gates those features on the `APP_DEBUG` environment variable
 * instead, so each environment opts in explicitly (Platypus deploys already set
 * `APP_DEBUG: true` in `application.yml`).
 *
 * Resolution order:
 * 1. `process.env.APP_DEBUG` when defined — server runtime, and explicit
 *    overrides in Jest
 * 2. `window.__APP_DEBUG__` — client side, bootstrapped by the host app's
 *    `_document.tsx` inline script alongside `__CURRENT_ENV__` (`APP_DEBUG` is
 *    not inlined into client bundles; builds are environment-agnostic)
 * 3. Jest default: enabled, mirroring the `APP_DEBUG === 'true' || NODE_ENV ===
 *    'test'` convention used by the GraphQL contexts
 * 4. Local dev default: enabled, so the Apollo sandbox and dev tooling work
 *    under `next dev` without extra configuration
 */
export function isAppDebug(): boolean {
    if (typeof process !== 'undefined' && process.env?.APP_DEBUG !== undefined) {
        return process.env.APP_DEBUG === 'true';
    }

    if (typeof window !== 'undefined' && window.__APP_DEBUG__ !== undefined) {
        return window.__APP_DEBUG__ === true;
    }

    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
        return true;
    }

    return isLocalEnv();
}

declare global {
    interface Window {
        __APP_DEBUG__?: boolean;
    }
}
